import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, MapPin, ShieldCheck, Loader2, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Metadata for the two supported corridors — keeps colour and label logic in one place.
const CORRIDOR_META = {
  'pink-line': {
    label: 'Pink Line Metro (BT Road \u2192 Kalyani Expressway)',
    tag: 'Pink Line',
    accent: 'from-pink-600 via-pink-700 to-slate-900',
    ring: 'ring-pink-400/40',
    button: 'bg-pink-600 hover:bg-pink-700',
    pillBg: 'bg-pink-500/20 border-pink-300/50 text-pink-100',
  },
  'kalyani-airport': {
    label: 'Kalyani Greenfield Airport corridor',
    tag: 'Kalyani Airport',
    accent: 'from-sky-700 via-sky-800 to-slate-900',
    ring: 'ring-sky-400/40',
    button: 'bg-sky-600 hover:bg-sky-700',
    pillBg: 'bg-sky-500/20 border-sky-300/50 text-sky-100',
  },
};

const AMOUNT_OPTIONS = [
  { v: '10',  l: 'Up to \u20B910 L (1\u20132 kathas)' },
  { v: '20',  l: '\u20B910 \u2013 \u20B920 L' },
  { v: '50',  l: '\u20B920 \u2013 \u20B950 L' },
  { v: '100', l: '\u20B950 L \u2013 \u20B91 Cr' },
  { v: '500', l: '\u20B91 Cr+' },
];

export const CorridorReserveModal = ({ corridor, onClose }) => {
  const meta = CORRIDOR_META[corridor];
  const [name, setName]   = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [amount, setAmount] = useState('20');
  const [note, setNote]   = useState('');
  const [busy, setBusy]   = useState(false);
  const [ok,   setOk]     = useState(false);
  const [err,  setErr]    = useState('');

  if (!meta) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim() || busy) return;
    setBusy(true); setErr('');
    try {
      await axios.post(`${BACKEND_URL}/api/invest/waitlist`, {
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,
        amount_range: amount,
        corridor,
        note: note.trim() || undefined,
      });
      setOk(true);
    } catch (e2) {
      // Never lose a lead — keep offline copy just in case
      try {
        const prev = JSON.parse(localStorage.getItem('am_invest_waitlist') || '[]');
        prev.push({ name, phone, email, amount_range: amount, corridor, note, ts: Date.now(), offline: true });
        localStorage.setItem('am_invest_waitlist', JSON.stringify(prev));
      } catch { /* ignore */ }
      setOk(true);
    } finally {
      setBusy(false);
    }
  };

  const modal = (
    <div className="fixed inset-0 z-[9999] grid place-items-center p-4 bg-slate-900/70 backdrop-blur-sm" data-testid="corridor-reserve-modal">
      <div className={`w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl ring-1 ${meta.ring}`}>
        {/* Header */}
        <div className={`bg-gradient-to-r ${meta.accent} text-white p-5 flex items-start gap-3`}>
          <div className="w-10 h-10 rounded-xl bg-white/15 border border-white/25 grid place-items-center shrink-0">
            <MapPin size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest border rounded-full px-2 py-0.5 ${meta.pillBg}`}>
              Reserve on the {meta.tag} corridor
            </div>
            <h3 className="text-lg sm:text-xl font-black mt-1 truncate">{meta.label}</h3>
            <div className="text-[11px] text-white/80 mt-0.5">Waitlist members get first-access + a locked-for-life 3% lower entry commission.</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white p-1 -mr-1" aria-label="Close" data-testid="corridor-modal-close">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="bg-white p-5">
          {ok ? (
            <div className="text-center py-6" data-testid="corridor-modal-success">
              <div className="w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mx-auto">
                <CheckCircle2 size={30} className="text-emerald-600" />
              </div>
              <h4 className="text-lg font-black text-slate-900 mt-3">You&rsquo;re on the {meta.tag} waitlist</h4>
              <p className="text-sm text-slate-600 mt-1.5">We&rsquo;ll reach out when the corridor parcels are ready. Keep an eye on your WhatsApp.</p>
              <button type="button" onClick={onClose} className={`mt-4 inline-flex items-center gap-2 ${meta.button} text-white font-bold text-sm px-5 py-2.5 rounded-full`} data-testid="corridor-modal-done">
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid gap-3" data-testid="corridor-modal-form">
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Full name *</label>
                <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Your full name" className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm" data-testid="corridor-modal-name" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Mobile *</label>
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} required inputMode="tel" placeholder="10-digit mobile" className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm" data-testid="corridor-modal-phone" />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Email (optional)</label>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="you@example.com" className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm" data-testid="corridor-modal-email" />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Interested budget</label>
                <select value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm bg-white" data-testid="corridor-modal-amount">
                  {AMOUNT_OPTIONS.map((o) => <option key={o.v} value={o.v}>Interested in: {o.l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Anything specific? (optional)</label>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Preferred station / catchment, plot use, timing…" className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm" data-testid="corridor-modal-note" />
              </div>

              {err && <div className="text-xs text-red-600 font-semibold">{err}</div>}

              <button type="submit" disabled={busy} className={`mt-1 inline-flex items-center justify-center gap-2 ${meta.button} text-white font-black text-sm px-5 py-3 rounded-full shadow-lg disabled:opacity-50`} data-testid="corridor-modal-submit">
                {busy ? <><Loader2 size={16} className="animate-spin" /> Reserving…</> : <>Reserve my seat on the {meta.tag} corridor</>}
              </button>

              <div className="flex items-start gap-2 text-[10px] text-slate-500 mt-1">
                <ShieldCheck size={12} className="text-emerald-600 shrink-0 mt-0.5" />
                <span>Prototype waitlist — no money will be collected. SEBI SM-REIT approval targeted 2027–2030.</span>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default CorridorReserveModal;
